import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Save, Wallet } from 'lucide-react'
import { createVendor, getVendors } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import OnboardingContinuePrompt from '../../components/onboarding/OnboardingContinuePrompt'
import OnboardingStepBar from '../../components/onboarding/OnboardingStepBar'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { AddressFields, Field, useOnboardingStep, validEmail } from './masterShared'
import { returnTo } from '../../components/CreateOption'

const BLANK = { name: '', contact_name: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: '' }

// One vendor's details. Their bills, payments and balance are under
// Purchase (the vendor's account).
export default function VendorForm() {
  const { vendorId } = useParams()
  const isNew = !vendorId
  const nav = useNavigate()
  // Opened from "+ Add a new …" in a dropdown: go back there afterwards.
  const done = returnTo(useLocation().search, '/master/vendors')
  const qc = useQueryClient()
  const { notify } = useToast()
  const { roles = [] } = useSession()
  const onboarding = useOnboardingStep('vendors')
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew)
  const seesAccount = ['admin', 'purchase', 'accounting'].some((r) => roles.includes(r))

  useEffect(() => {
    if (isNew) { setForm(BLANK); return }
    setLoading(true)
    getVendors()
      .then((rows) => {
        const v = (rows || []).find((r) => String(r.vendor_id) === String(vendorId))
        if (!v) { setError('This vendor wasn’t found.'); return }
        setForm(Object.fromEntries(Object.keys(BLANK).map((k) => [k, v[k] || ''])))
      })
      .catch((e) => setError(e.message || 'Couldn’t load the vendor.'))
      .finally(() => setLoading(false))
  }, [vendorId, isNew])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Give the vendor a name.'
    if (!validEmail(form.email)) e.email = 'That email doesn’t look right.'
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      await createVendor({ ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, String(v).trim()])), vendor_id: isNew ? undefined : Number(vendorId) })
      await qc.invalidateQueries({ queryKey: ['vendors'] })
      notify(isNew ? `${form.name.trim()} added` : 'Vendor saved')
      if (onboarding.afterSave()) { if (isNew) { setForm(BLANK); setErrors({}) } return }
      nav(done)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save the vendor.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading vendor…" /></div>

  return (
    <div className="ci-form-page">
      <OnboardingStepBar step="vendors" />
      <DocHeader
        back={<BackLink to={done} label={done === '/master/vendors' ? 'Vendors' : 'Back'} />}
        title={isNew ? 'New vendor' : (form.name || 'Vendor')}
        meta={isNew ? 'A supplier you buy goods or services from.' : [form.contact_name, form.email || form.phone].filter(Boolean).join(' · ')}
        actions={!isNew && seesAccount && <Link className="ci-btn ci-btn-ghost" to={`/purchase/ap/vendor/${vendorId}`}><Wallet size={15} />Bills and payments</Link>}
      />
      {error && <ErrorBanner message={error} />}
      <div className="ci-form-meta-card mf-form">
        <div className="ci-form-meta-top two">
          <Field label="Vendor name" error={errors.name}>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Lagos Textiles Ltd" />
          </Field>
          <Field label="Contact person (optional)">
            <input value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} />
          </Field>
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Email (optional)" error={errors.email}>
            <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="orders@example.com" />
          </Field>
          <Field label="Phone (optional)">
            <input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
        </div>
        <AddressFields form={form} set={set} />
      </div>
      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add vendor' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(done)}>Cancel</button>
        </div>
      </div>
      <OnboardingContinuePrompt open={onboarding.open} saved="Vendor saved" another="Add another vendor" next={onboarding.after} onAddAnother={onboarding.addAnother} onNext={onboarding.next} />
    </div>
  )
}
